import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/api/apiClient";
import { useQuery } from "@tanstack/react-query";
import { useCategories } from "@/hooks/useCategories";
import { m, AnimatePresence } from "framer-motion";
import { getOptimizedImageUrl, getRemoteImageUrl } from "@/utils";
import {
    Compass,
    Heart,
    Sparkles,
    ChevronRight,
    Flame,
    TrendingUp,
    ArrowRight,
    Play,
    Zap,
    Bot,
} from "lucide-react";

import { useOnboarding } from "@/hooks/useOnboarding";
import { OnboardingChecklist } from "@/components/home/OnboardingChecklist";


// ─── Brand Focus Card ───────────────────────────────────────
function BrandFocusCard({ brand, onClick }) {
    if (!brand) return null;

    return (
        <m.button
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 0.5 }}
            onClick={onClick}
            className="home-brand-focus"
        >
            <div className="home-brand-focus-badge">
                <Sparkles className="w-3.5 h-3.5" />
                <span>Marque du jour</span>
            </div>
            <div className="home-brand-focus-content">
                {brand.logo_url ? (
                    <img
                        src={getRemoteImageUrl(brand.logo_url)}
                        alt={brand.name}
                        className="home-brand-logo"
                    />
                ) : (
                    <div className="home-brand-logo-placeholder">
                        {brand.name?.charAt(0)?.toUpperCase()}
                    </div>
                )}
                <div className="home-brand-focus-info">
                    <h3 className="home-brand-name">{brand.name}</h3>
                    {brand.description && (
                        <p className="home-brand-desc">{brand.description}</p>
                    )}
                </div>
                <ChevronRight className="w-5 h-5 text-amber-400 shrink-0" />
            </div>
        </m.button>
    );
}

// ─── Product Preview Row ────────────────────────────────────
function ProductPreviewRow({ products, title, icon: Icon, onProductClick }) {
    if (!products || products.length === 0) return null;

    return (
        <m.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="home-preview-section"
        >
            <div className="home-preview-header">
                <Icon className="w-4 h-4 text-amber-500" />
                <span className="home-preview-title">{title}</span>
            </div>
            <div className="home-preview-grid">
                {products.slice(0, 4).map((product, i) => (
                    <m.div
                        key={product.id}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.7 + i * 0.1, duration: 0.4 }}
                        className="home-preview-card"
                        onClick={() => onProductClick && onProductClick(product.id)}
                        style={{ cursor: 'pointer' }}
                        whileHover={{ scale: 1.04 }}
                        whileTap={{ scale: 0.97 }}
                    >
                        <img
                            src={getOptimizedImageUrl(product.images?.[0], 200)}
                            alt={product.name}
                            className="home-preview-img"
                            loading="lazy"
                        />
                        <div className="home-preview-overlay">
                            <span className="home-preview-price">
                                {product.price}€
                            </span>
                        </div>
                    </m.div>
                ))}
            </div>
        </m.div>
    );
}

// ═══════════════════════════════════════════════════════════════
//  HOME PAGE COMPONENT
// ═══════════════════════════════════════════════════════════════
export default function HomePage() {
    const navigate = useNavigate();
    const { categories, isAuthenticated, user } = useCategories();
    const { streakCount, completeTask } = useOnboarding();

    // ── Data Queries ─────────────────────────────────────────
    const { data: interactions = [] } = useQuery({
        queryKey: ["interactions", "likes"],
        queryFn: async () => {
            try { await apiClient.auth.me(); } catch (e) { /* Guest */ }
            // Same filter as Matches page — only count actual likes
            return apiClient.entities.UserInteraction.list({ action: 'swipe_right,like' });
        },
        refetchOnMount: 'always',
    });

    const { data: brands = [] } = useQuery({
        queryKey: ["brandsList"],
        queryFn: () => apiClient.brands.list(),
    });

    const { data: showcaseProducts = [] } = useQuery({
        queryKey: ["showcaseHome"],
        queryFn: () => apiClient.entities.Product.showcase(2),
    });

    // ── Derived Stats ────────────────────────────────────────
    const likedCount = interactions.length;

    const collectionsCount = useMemo(() => {
        return [...new Set(categories.map(c => c.category_name))].filter(Boolean).length;
    }, [categories]);

    const uniqueBrandsDiscovered = useMemo(() => {
        const brandNames = interactions
            .map(i => i.product?.brand || i.product?.brand_name || i.brand_name)
            .filter(Boolean);
        return [...new Set(brandNames)].length;
    }, [interactions]);

    // ── Daily Focus Brand (pick a pseudo-random brand every day) ──
    const focusBrand = useMemo(() => {
        if (!brands || brands.length === 0) return null;
        
        const now = new Date();
        const year = now.getFullYear();
        const month = now.getMonth();
        const day = now.getDate();
        
        const dateSeed = year * 10000 + (month + 1) * 100 + day;
        const hash = (dateSeed * 15485863) % 1000000; 
        const index = hash % brands.length;
        
        return brands[index];
    }, [brands]);

    // ── Resume Session ───────────────────────────────────────
    const savedIndex = parseInt(localStorage.getItem("swipeCurrentIndex") || "0", 10);
    const hasSession = savedIndex > 0;

    // ── Greeting ─────────────────────────────────────────────
    const greeting = useMemo(() => {
        const hour = new Date().getHours();
        if (hour < 12) return "Bonjour";
        if (hour < 18) return "Bon après-midi";
        return "Bonsoir";
    }, []);

    const userName = user?.name || user?.first_name || null;
    const isNewUser = likedCount === 0 && collectionsCount === 0;

    return (
        <div className="home-page">
            <div className="home-container">
                {/* ── Header with Streak ──────────────────────── */}
                <div className="flex justify-between items-center mb-4 mt-4">
                    <m.h1 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2"
                    >
                        {greeting}{userName ? `, ${userName}` : ""} <span className="home-wave">👋</span>
                    </m.h1>
                    {streakCount > 0 && (
                        <m.div 
                            initial={{ opacity: 0, scale: 0.8 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex items-center gap-2 bg-amber-100 dark:bg-amber-500/20 text-amber-800 dark:text-amber-400 px-3 py-1.5 rounded-full border border-amber-300 dark:border-amber-500/30"
                        >
                            <Flame className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                            <span className="font-semibold text-sm">{streakCount} {streakCount > 1 ? 'jours' : 'jour'}</span>
                        </m.div>
                    )}
                </div>

                {/* ── Hero Text (smaller) + CTA Button (big, right below) ── */}
                <m.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="home-hero mb-6"
                >
                    <p className="text-base md:text-lg text-gray-600 dark:text-gray-300 leading-relaxed mb-5">
                        Pépite, le site pour retrouver les meilleurs vêtements de marques indépendantes.
                    </p>
                    
                    {/* ── Big CTA Button ── */}
                    <m.button
                        initial={{ opacity: 0, y: 15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.5, ease: "easeOut" }}
                        whileHover={{ scale: 1.03 }}
                        whileTap={{ scale: 0.97 }}
                        onClick={() => {
                            localStorage.setItem("swipeCurrentIndex", "0");
                            localStorage.removeItem("swipeProducts");
                            navigate("/swipe");
                        }}
                        className="home-hero-cta"
                    >
                        <Compass className="w-6 h-6" />
                        <span>Commencer à découvrir</span>
                        <ArrowRight className="w-5 h-5 opacity-70" />
                    </m.button>

                    {hasSession && (
                        <m.button
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.45, duration: 0.4 }}
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => navigate("/swipe")}
                            className="home-resume-cta"
                        >
                            <Play className="w-4 h-4" />
                            <span>Continuer où j'en étais (produit n°{savedIndex + 1})</span>
                        </m.button>
                    )}
                </m.div>

                {/* ── Personal Shopper Card ──────────────────── */}
                <m.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.35, duration: 0.5 }}
                    onClick={() => navigate('/shopper')}
                    className="relative overflow-hidden bg-stone-900 border border-stone-800 rounded-2xl p-6 mb-8 cursor-pointer shadow-md group flex flex-col md:flex-row items-center gap-6"
                >
                    <div className="flex-shrink-0 relative">
                        <div className="w-14 h-14 bg-stone-800 rounded-xl flex items-center justify-center border border-stone-700 shadow-inner">
                            <Bot className="w-7 h-7 text-amber-400" />
                        </div>
                        <div className="absolute -bottom-2 -right-2 px-2 py-0.5 bg-amber-600 text-white text-[10px] font-bold uppercase tracking-widest rounded-full shadow-sm">
                            Beta
                        </div>
                    </div>
                    
                    <div className="flex-1 text-center md:text-left z-10">
                        <h3 className="text-xl font-bold text-white mb-2 flex items-center justify-center md:justify-start gap-2">
                            Personal Shopper IA <Sparkles className="w-4 h-4 text-amber-400" />
                        </h3>
                        <p className="text-sm text-stone-300 mb-4 md:mb-0">
                            Discutez avec notre assistant intelligent pour trouver la pièce idéale selon vos goûts.
                        </p>
                    </div>

                    <div className="flex-shrink-0 z-10 w-full md:w-auto">
                        <button className="w-full md:w-auto px-6 py-2.5 bg-white text-stone-900 font-semibold rounded-xl hover:bg-stone-100 transition-colors flex items-center justify-center gap-2 shadow-sm">
                            Essayer
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </div>
                </m.div>

                {/* ── Onboarding Checklist ──────────────────── */}
                <OnboardingChecklist focusBrand={focusBrand} />



                {/* ── Brand Focus ─────────────────────────── */}
                {focusBrand && (
                    <BrandFocusCard
                        brand={focusBrand}
                        onClick={() => {
                            completeTask('view_brand_of_day');
                            navigate(`/brand/${focusBrand.slug}`);
                        }}
                    />
                )}

                {/* ── Product Previews ────────────────────── */}
                {showcaseProducts.length > 0 && (
                    <ProductPreviewRow
                        products={showcaseProducts}
                        title="Récemment ajoutés"
                        icon={Sparkles}
                        onProductClick={(id) => navigate(`/product?id=${id}`, { state: { from: 'home' } })}
                    />
                )}
            </div>
        </div>
    );
}